import React, { useEffect, useState } from "react";
import { apiUrl, executeRequest, getToken } from "../../functions/requests";
import { usePageState } from "../../Layout";
import "../../css/KnowledgeBase.css";
import Spacer from "../../components/Spacer";
import IconPlus from "../../icons/IconPlus";
import List from "../../components/List";
import Button from "../../components/Button";
import KnowledgeBaseRecordUpdate from "./KnowledgeBaseRecordUpdate";
import KnowledgeBaseRecordCreate from "./KnowledgeBaseRecordCreate";
import KnowledgeBaseRecordDelete from "./KnowledgeBaseRecordDelete";
import KnowledgeBaseRecord from "./KnowledgeBaseRecord";
import IconClose from "../../icons/IconClose";
import IconLevelUp from "../../icons/IconLevelUp";
import IconReload from "../../icons/IconReload";
import IconEdit from "../../icons/IconEdit";
import KnowledgeBaseTrash from "./KnowledgeBaseTrash";
import Popup from "../../components/Popup";

export default function KnowledgeBase() {
  const { pageState, setPageState } = usePageState();
  const [userData, setUserData] = useState(pageState.userData);

  const [records, setRecords] = useState([]);

  const [specificRecord, setSpecificRecord] = useState(null);
  const [specificDir, setSpecificDir] = useState("/");
  const [specificDirRecords, setSpecificDirRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [recordToUpdate, setRecordToUpdate] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [createNewRecord, setCreateNewRecord] = useState(false);

  const [showTrash, setShowTrash] = useState(false);

  // Get all records in knowledge base and store them to page state
  const getRecords = async () => {
    setLoading(true);
    await executeRequest(
      apiUrl + "knowledge-base-records",
      "GET",
      null,
      {
        Authorization: "Bearer " + getToken(),
      },
      (data) => {
        console.log(data);
        setRecords(data);
        // If selected record, refresh it's data
        if (specificRecord) {
          // Find it in retrieved records
          var newSpecificRecordData = data.filter(
            (record) => record.uuid === specificRecord.uuid
          );
          if (newSpecificRecordData.length > 0) {
            // Replace it's data with the new
            setSpecificRecord(newSpecificRecordData[0]);
          } else {
            // The record does not exist anymore, unset it
            setSpecificRecord(null);
          }
        }
      },
      (error) => {
        console.log(error);
      }
    );
    setLoading(false);
  };

  // Delete record
  const deleteRecord = async () => {
    setLoading(true);
    await executeRequest(
      apiUrl + "knowledge-base-records/" + recordToDelete.uuid,
      "DELETE",
      null,
      {
        Authorization: "Bearer " + getToken(),
      },
      (data) => {
        getRecords();
        setRecordToDelete(null);
      },
      (error) => {}
    );
    setLoading(false);
  };

  // Filter records of the current directory
  const filterRecordsByPath = (records, specificDir) => {
    return records.filter((record) => record.path === specificDir);
  };

  // Starting functions
  useEffect(() => {
    getRecords(); // Get initial records
  }, []);

  // Refresh filtered records whenever specificDir changes
  useEffect(() => {
    setSpecificDirRecords(filterRecordsByPath(records, specificDir));
  }, [specificDir, records]);

  // HTML rendering
  if (recordToUpdate !== null) {
    return (
      <>
        <Button
          text="< Back"
          type="underlined"
          onClicked={() => setRecordToUpdate(null)}
        />
        <KnowledgeBaseRecordUpdate
          record={recordToUpdate}
          onUpdated={() => getRecords()}
        />
      </>
    );
  }

  if (createNewRecord !== false) {
    return (
      <>
        <Button
          text="< Back"
          type="underlined"
          onClicked={() => setCreateNewRecord(false)}
        />
        <KnowledgeBaseRecordCreate
          directory={specificDir}
          onCreated={() => {
            getRecords();
            setCreateNewRecord(false);
          }}
        />
      </>
    );
  }

  if (showTrash) {
    return (
      <>
        <Button
          text="< Back"
          type="underlined"
          onClicked={() => setShowTrash(false)}
        />
        <Spacer />
        <KnowledgeBaseTrash onActionExecuted={() => getRecords()} />
      </>
    );
  }

  return (
    <div>
      <h1 className="page-title">Knowledge Base</h1>
      <div className="flex flex-gap-s">
        <Button
          text="Add new"
          type="success"
          icon={<IconPlus />}
          onClicked={() => setCreateNewRecord(true)}
        />
        <Button
          text="Reload"
          type="info"
          icon={<IconReload />}
          loading={loading}
          onClicked={() => getRecords()}
        />
        {specificRecord !== null &&
          userData.uuid === specificRecord.owner.uuid && (
            <Button
              type="info"
              icon={<IconEdit />}
              text="Edit record"
              onClicked={() => setRecordToUpdate(specificRecord)}
            />
          )}
        <Button
          text="Trash"
          type="danger"
          icon={<IconClose />}
          onClicked={() => setShowTrash(true)}
        />
        {(specificDir !== "/" || specificRecord !== null) && (
          <Button
            icon={<IconLevelUp />}
            text="Up 1 level"
            type="underlined"
            onClicked={() => {
              if (specificRecord !== null) {
                setSpecificRecord(null);
              } else {
                setSpecificDir((prevDir) => {
                  // Remove trailing slash if exists, split by "/"
                  const parts = prevDir.replace(/\/$/, "").split("/");
                  // Remove the last segment
                  parts.pop();
                  // Join back together with a trailing slash
                  return parts.length > 0 ? parts.join("/") + "/" : "/";
                });
              }
            }}
          />
        )}
      </div>
      <Spacer />
      <div className="flex flex-gap-s flex-align-center">
        <div>Location:</div>
        <div>
          {specificRecord === null
            ? specificDir
            : specificRecord.path + specificRecord.name}
        </div>
      </div>
      <Spacer />
      {specificRecord !== null ? (
        <KnowledgeBaseRecord
          record={specificRecord}
          onLinkedRecordClicked={(linkedRecord) => {
            console.log(linkedRecord.path + linkedRecord.name);
            setSpecificDir(linkedRecord.path);
            setSpecificRecord(linkedRecord);
          }}
        />
      ) : (
        <List
          itemType="knowledge-base-record"
          items={records.filter((item) => item.path === specificDir)}
          headers={[
            "Name",
            "Type",
            "Description",
            "Owner",
            "Created at",
            "Updated at",
          ]}
          isLoading={loading}
          onView={(item) => {
            if (item.type === "file") {
              setSpecificRecord(item);
            }
            if (item.type === "dir") {
              setSpecificDir(item.path + item.name + "/");
            }
          }}
          onEdit={(item) => setRecordToUpdate(item)}
          onDelete={(item) => setRecordToDelete(item)}
        />
      )}
      {recordToDelete !== null && (
        <>
          <Popup
            isActive={true}
            title={`Delete record ${recordToDelete.name}`}
            content={
              <>
                <div style={{ textAlign: "center" }}>
                  Are you sure you want to delete this record? If it is a
                  folder, all files under it will be deleted.
                </div>
                <Spacer />
                <div className="flex flex-gap-s flex-justify-center">
                  <Button
                    text="Confirm"
                    type="success"
                    onClicked={() => deleteRecord()}
                    disabled={loading}
                    loading={loading}
                  />
                  <Button
                    text="Cancel"
                    type="danger"
                    onClicked={() => setRecordToDelete(null)}
                    disabled={loading}
                    loading={loading}
                  />
                </div>
              </>
            }
            onClosed={() => setRecordToDelete(null)}
          />
        </>
      )}
    </div>
  );
}
